import { NavLink, useNavigate } from "react-router-dom";
import { Home, Inbox as InboxIcon, CalendarCheck, CalendarDays, Menu, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Mobile-only bottom navigation bar (PWA-style).
 * 5 primary tabs + floating AI assistant FAB.
 * Hidden on desktop (lg+) — Layout sidebar takes over there.
 */
const TABS = [
  { to: "/",           label: "Home",       icon: Home,          end: true,  testid: "mobile-nav-home" },
  { to: "/inbox",      label: "Inbox",      icon: InboxIcon,                 testid: "mobile-nav-inbox" },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck,             testid: "mobile-nav-attendance" },
  { to: "/calendar",   label: "Calendar",   icon: CalendarDays,              testid: "mobile-nav-calendar" },
  { to: "#more",       label: "More",       icon: Menu,                      testid: "mobile-nav-more", openSidebar: true },
];

export default function MobileBottomNav({ onOpenSidebar }) {
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/inbox").then((r) => setUnread((r.data || []).filter(n => !n.read).length)).catch(() => {});
  }, []);

  return (
    <>
      {/* Floating AI assistant FAB — sits ABOVE the bottom nav on mobile */}
      <button
        onClick={() => nav("/ai")}
        className="qh-grad-bg lg:hidden fixed right-4 bottom-[88px] z-40 w-14 h-14 rounded-full text-white shadow-2xl shadow-violet-500/40 flex items-center justify-center active:scale-95 transition-transform"
        data-testid="mobile-ai-fab"
        aria-label="Qrew AI Assistant"
      >
        <Sparkles className="w-6 h-6" />
      </button>

      {/* Bottom nav bar */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="mobile-bottom-nav"
      >
        <div className="grid grid-cols-5">
          {TABS.map((t) => {
            const Icon = t.icon;
            if (t.openSidebar) {
              return (
                <button
                  key={t.label}
                  onClick={() => onOpenSidebar?.()}
                  className="relative flex flex-col items-center justify-center gap-0.5 h-16 text-slate-500 hover:text-violet-700 active:scale-95 transition-all"
                  data-testid={t.testid}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] font-semibold">{t.label}</span>
                </button>
              );
            }
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-0.5 h-16 transition-all ${
                    isActive ? "text-violet-700" : "text-slate-500 hover:text-violet-600"
                  }`
                }
                data-testid={t.testid}
              >
                {({ isActive }) => (
                  <>
                    <div className="relative">
                      <Icon className="w-5 h-5" />
                      {t.to === "/inbox" && unread > 0 && (
                        <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center border border-white">
                          {unread > 9 ? "9+" : unread}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] font-semibold">{t.label}</span>
                    {isActive && <span className="absolute bottom-1 w-6 h-0.5 rounded-full bg-violet-600" />}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Spacer so page content isn't hidden behind nav */}
      <div className="lg:hidden h-20" aria-hidden />
    </>
  );
}
